import React, { useContext } from 'react';
import { useRecoilState } from 'recoil';
import { AuthInfoContext } from '../../AuthContextProvider';
import { ROLE } from '../../const/Const';
import { mainBlueColor } from '../../GlobalState';
import { styled } from '@mui/material/styles';
import {
  Checkbox,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Button,
  Paper,
  Divider
} from '@mui/material';
import { CustomTooltip } from '../tooltip/CustomTooltip';
import HelpIcon from '@material-ui/icons/Help';
import NumberInput from '../NumberInput';
import { parsePrice } from '../../utils';
import _ from 'lodash';
import { tooltipContents } from '../tooltip/Const';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  '&.MuiTableCell-head': {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    borderRight: '1px #CCCCCC solid'
  },
  '&.MuiTableCell-body': {
    fontSize: 16,
    padding: 0,
    height: 60,
    borderRight: '1px #CCCCCC solid',
    backgroundColor: '#F5F5F5'
  }
}));

type Estimate = {
  [key: string]: any;
  id: number;
  quantity: number;
  code: string;
  billingTotal: number;
  billingTotalSum: number;
  invoicePrice: number;
};

type TProps = {
  source: string;
  estimates: Estimate[];
  setEstimates: React.Dispatch<React.SetStateAction<Estimate[]>>;
  constructionEstimates: Estimate[];
  outPutDetails: boolean;
  setOutPutDetails: (value: React.SetStateAction<boolean>) => void;
  memo: string;
  setMemo: React.Dispatch<React.SetStateAction<string>>;
  remarks: string;
  setRemarks: React.Dispatch<React.SetStateAction<string>>;
};

export const EstimatesTable: React.FC<TProps> = ({
  source,
  estimates,
  setEstimates,
  constructionEstimates,
  outPutDetails,
  setOutPutDetails,
  memo,
  setMemo,
  remarks,
  setRemarks
}) => {
  const [authInfo] = useContext(AuthInfoContext);
  const baseEstimates = {
    id: 0,
    quantity: 1,
    code: '選択してください',
    billingTotal: 0,
    billingTotalSum: 0,
    invoicePrice: 0
  };
  const [mainColor] = useRecoilState(mainBlueColor);

  const outPutDetail = () => {
    if (outPutDetails) {
      setOutPutDetails(false);
    } else {
      setOutPutDetails(true);
    }
  };

  const updateListOfEstimates = (index: number, key: string, value: string) => {
    if (value === '0') return;
    let copyEstimates = _.cloneDeep(estimates);
    copyEstimates[index][key] = value;
    if (key === 'id') {
      const selectedEstimate: any = constructionEstimates.filter(
        (obj: any) => obj.code.toString() === value
      );

      if (value != '見積書なし') {
        copyEstimates[index].id = selectedEstimate[0].id;
        copyEstimates[index].estimate_id = parseInt(selectedEstimate[0].code);
        copyEstimates[index].code = selectedEstimate[0].code;
        copyEstimates[index].billingTotal = selectedEstimate[0].billing_total;
        copyEstimates[index].constructionName =
          selectedEstimate[0].construct_sub;
      } else {
        copyEstimates[index].id = -1;
        copyEstimates[index].code = '見積書なし';
        copyEstimates[index].billingTotal = 0;
        copyEstimates[index].constructionName = '';
      }
    }
    if (key !== 'constructionName') {
      copyEstimates[index].billingTotalSum = copyEstimates[index].billingTotal;
    }
    setEstimates(copyEstimates);
  };

  const deleteEstimate = (index: number) => {
    let copyEstimates = _.cloneDeep(estimates);
    copyEstimates.splice(index, 1);
    setEstimates(copyEstimates);
  };

  const addNewEstimates = () => {
    setEstimates([...estimates, baseEstimates]);
  };

  const handleInputChange = (index: number, event: any) => {
    const newEstimates = [...estimates];
    const inputValue = event.target.value;

    newEstimates[index].invoicePrice = parseInt(inputValue);
    newEstimates[index].billingTotalSum = parseInt(inputValue);

    setEstimates(newEstimates);
  };

  const handleSelectChange = (
    index: number,
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    updateListOfEstimates(index, 'id', event.target.value);
  };

  return (
    <div style={{ backgroundColor: '#FFFFFF' }}>
      <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 5 }}>
        <Checkbox
          checked={outPutDetails}
          onChange={outPutDetail}
          color='default'
        />
        <span style={{ marginBottom: 3 }}>請求書に明細を表示する</span>
        <CustomTooltip title={tooltipContents.addDetails} placement='right'>
          <HelpIcon color='action' style={{ marginLeft: 8 }} />
        </CustomTooltip>
      </div>
      <div style={{ paddingRight: 15, paddingBottom: 15, paddingLeft: 15 }}>
        <TableContainer component={Paper} style={{ marginBottom: 15 }}>
          <Table aria-label='customized table'>
            <TableHead>
              <TableRow>
                <EstimatesTableTitle width={150} mainColor={mainColor}>
                  見積番号
                </EstimatesTableTitle>
                <EstimatesTableTitle width={300} mainColor={mainColor}>
                  工事件名
                </EstimatesTableTitle>
                <EstimatesTableTitle width={100} mainColor={mainColor}>
                  金額（税抜）
                </EstimatesTableTitle>
                <EstimatesTableTitle width={50} mainColor={mainColor}>
                  削除
                </EstimatesTableTitle>
              </TableRow>
            </TableHead>
            <TableBody>
              {estimates.map((estimate: Estimate, index: number) => (
                <TableRow key={index}>
                  <EstimatesTableBody align='center'>
                    <EstimatesTableCell>
                      <SelectBoxStyle
                        value={estimate.code}
                        onChange={event => handleSelectChange(index, event)}
                        form='carform'
                      >
                        <option value={0}>選択してください</option>
                        {constructionEstimates
                          .filter(
                            (i: any) =>
                              !(
                                i.status === 5 &&
                                !estimates.some((e: Estimate) => e.id === i.id)
                              )
                          )
                          .map((i: any) => (
                            <option value={i.code}>
                              {i.code === '見積書なし'
                                ? i.code
                                : source === 'customer'
                                ? `${i.code} - ${i.construct_sub}`
                                : i.code}
                              {i.isIssued === 1 && ' (請求書発行済み)'}
                            </option>
                          ))}
                      </SelectBoxStyle>
                    </EstimatesTableCell>
                  </EstimatesTableBody>
                  <EstimatesTableBody align='center'>
                    <EstimatesTableCell>
                      <StyledInput
                        value={estimate.constructionName}
                        placeholder='60文字以内'
                        maxLength={60}
                        type='text'
                        onChange={text => {
                          updateListOfEstimates(
                            index,
                            'constructionName',
                            text.target.value
                          );
                        }}
                      />
                    </EstimatesTableCell>
                  </EstimatesTableBody>
                  <EstimatesTableBody align='right'>
                    <FixedTextContainer>
                      {estimates[index].code === '見積書なし' ? (
                        <StyledNumberInput
                          symbol=''
                          value={
                            estimate.billingTotalSum || estimate.invoicePrice
                          }
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            handleInputChange(index, e);
                          }}
                        />
                      ) : (
                        parsePrice(estimate.billingTotalSum, '')
                      )}
                    </FixedTextContainer>
                  </EstimatesTableBody>
                  <EstimatesTableBody align='center'>
                    <Button
                      style={{
                        color: '#758283',
                        backgroundColor: '#F5F5F5'
                      }}
                      variant='contained'
                      color='primary'
                      onClick={() => {
                        deleteEstimate(index);
                      }}
                    >
                      削除
                    </Button>
                  </EstimatesTableBody>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '10px 0px'
          }}
        >
          <CustomTooltip title={tooltipContents.invoice} placement='left'>
            <Button
              style={{ color: '#758283', backgroundColor: '#F5F5F5' }}
              variant='contained'
              color='primary'
              onClick={() => {
                addNewEstimates();
              }}
            >
              追加
            </Button>
          </CustomTooltip>
        </div>
        {authInfo?.role === ROLE.EXECUTIVE && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingLeft: 5,
              marginBottom: 20
            }}
          >
            <a
              href='https://labol.co.jp/lp/factoring?promo_code=ps2_conoc_1'
              target='_blank'
              rel='noopener noreferrer'
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <span style={{ marginBottom: 300, color: '#4285F4' }}>
                ファクタリングのご利用
              </span>
            </a>
            <CustomTooltip title={tooltipContents.factoring} placement='right'>
              <HelpIcon color='action' style={{ marginLeft: 8 }} />
            </CustomTooltip>
          </div>
        )}
        <div style={{ display: 'flex' }}>
          <RemarksContainer mainColor={mainColor}>
            <TextContainer>備考</TextContainer>
          </RemarksContainer>
          <RemarksMainContainer>
            <InputContainer>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <RemarksInput
                  value={remarks}
                  placeholder='200文字以内'
                  maxLength={200}
                  onChange={text => {
                    setRemarks(text.target.value);
                  }}
                />
                <div style={{ marginLeft: 20 }}></div>
              </div>
            </InputContainer>
          </RemarksMainContainer>
        </div>
        <Divider style={{ backgroundColor: '#CCCCCC' }} />
        <div style={{ display: 'flex' }}>
          <RemarksContainer mainColor={mainColor}>
            <TextContainer>メモ</TextContainer>
          </RemarksContainer>
          <RemarksMainContainer>
            <InputContainer>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <RemarksInput
                  value={memo}
                  onChange={text => {
                    setMemo(text.target.value);
                  }}
                />
                <div style={{ marginLeft: 20 }}></div>
              </div>
            </InputContainer>
          </RemarksMainContainer>
        </div>
      </div>
    </div>
  );
};

const RemarksContainer = styled('div')(
  ({ mainColor }: { mainColor: string }) => ({
    display: 'flex',
    backgroundColor: mainColor,
    height: 80,
    alignItems: 'center'
  })
);

const EstimatesTableTitle = styled(StyledTableCell)(
  ({ width, mainColor }: { width: number; mainColor: string }) => ({
    padding: 8,
    backgroundColor: `${mainColor} !important`,
    color: '#FFFFFF',
    fontWeight: 'bold',
    fontSize: 16,
    width: width,
    align: 'center'
  })
);

const EstimatesTableCell = styled('div')({
  marginLeft: 10,
  marginRight: 13
});

const EstimatesTableBody = styled(StyledTableCell)(
  ({ align }: { align: string }) => ({
    backgroundColor: '#F5F5F5',
    align: align
  })
);

const SelectBoxStyle = styled('select')({
  WebkitAppearance: 'menulist-button',
  backgroundColor: 'white',
  borderColor: '#c1c1c1',
  height: 48,
  width: '100%'
});

const StyledInput = styled(`input`)({
  height: 40,
  width: '100%',
  borderColor: '#c1c1c1',
  borderWidth: 1
});

const FixedTextContainer = styled(`div`)({
  fontSize: 14,
  marginLeft: 15,
  marginRight: 15
});

const StyledNumberInput = styled(NumberInput)({
  height: 40,
  width: '100%',
  borderColor: '#c1c1c1',
  borderWidth: 1
});

const TextContainer = styled(`div`)({
  marginLeft: 15,
  color: '#FFFFFF',
  fontSize: 14
});

const RemarksMainContainer = styled(`div`)({
  display: 'flex',
  backgroundColor: '#F5F5F5',
  height: 80,
  alignItems: 'center',
  width: '100%'
});

const InputContainer = styled(`div`)({
  marginLeft: 10,
  marginRight: 10,
  paddingRight: 10,
  width: '100%'
});

const RemarksInput = styled(`textarea`)({
  height: 60,
  maxHeight: 60,
  width: '100%',
  borderColor: '#c1c1c1',
  borderWidth: 1
});
